<template>
  <div class="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2">
    <transition name="chat-panel">
      <div
        v-if="isOpen"
        class="w-[min(100vw-2rem,380px)] h-[min(70vh,520px)] flex flex-col rounded-2xl border-2 border-stone-400/60 dark:border-amber-500/40 bg-white/90 dark:bg-stone-900/90 backdrop-blur-md shadow-2xl overflow-hidden"
      >
        <!-- Header -->
        <div
          class="flex items-center gap-3 px-4 py-3 border-b border-stone-200 dark:border-stone-700 bg-gradient-to-r from-stone-100/80 to-amber-50/60 dark:from-stone-800/80 dark:to-amber-900/30"
        >
          <div
            class="relative w-10 h-10 rounded-full bg-gradient-to-br from-stone-300 to-stone-500 dark:from-stone-600 dark:to-stone-800 flex items-center justify-center ring-2 ring-amber-400/50 shadow-inner"
          >
            <Icon icon="mdi:ghost" class="w-6 h-6 text-white/90" />
            <span
              class="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white dark:ring-stone-900 animate-pulse"
            />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-bold text-stone-800 dark:text-amber-200">{{ t("common.ghost") }}</p>
            <p class="text-xs text-stone-500 dark:text-stone-400 truncate">
              {{ t("chat.subtitle") }}
            </p>
          </div>
          <button
            type="button"
            @click="clearChat"
            class="p-1.5 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
            :title="t('chat.clearChat')"
          >
            <Icon icon="mdi:refresh" class="w-4 h-4" />
          </button>
          <button
            type="button"
            @click="isOpen = false"
            class="p-1.5 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
          >
            <Icon icon="mdi:close" class="w-5 h-5" />
          </button>
        </div>

        <!-- Messages -->
        <div
          ref="messagesRef"
          class="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        >
          <div
            v-for="msg in messages"
            :key="msg.id"
            :class="[
              'flex',
              msg.role === 'user' ? 'justify-end' : 'justify-start',
            ]"
          >
            <div
              :class="[
                'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                msg.role === 'user'
                  ? 'bg-amber-400/90 dark:bg-amber-500/80 text-stone-900 rounded-br-sm'
                  : 'bg-stone-100/90 dark:bg-stone-800/80 text-stone-700 dark:text-stone-200 border border-stone-200/60 dark:border-stone-600/40 rounded-bl-sm',
              ]"
            >
              <span
                v-if="msg.role === 'assistant'"
                class="text-[10px] font-bold text-amber-600 dark:text-amber-400 block mb-0.5"
              >
                {{ t("common.ghost") }}
              </span>
              {{ msg.content }}
            </div>
          </div>

          <div v-if="isLoading" class="flex justify-start">
            <div
              class="bg-stone-100/90 dark:bg-stone-800/80 rounded-2xl rounded-bl-sm px-4 py-3 border border-stone-200/60 dark:border-stone-600/40"
            >
              <div class="flex gap-1">
                <span
                  v-for="i in 3"
                  :key="i"
                  class="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce"
                  :style="{ animationDelay: `${(i - 1) * 150}ms` }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Input -->
        <form
          @submit.prevent="handleSubmit"
          class="px-3 py-3 border-t border-stone-200 dark:border-stone-700 bg-stone-50/50 dark:bg-stone-900/50"
        >
          <div class="flex gap-2">
            <input
              v-model="inputText"
              type="text"
              :placeholder="t('chat.placeholder')"
              :disabled="isLoading"
              class="flex-1 h-10 px-3 text-sm rounded-xl border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-800 dark:text-stone-100 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-400/50 disabled:opacity-50"
            />
            <button
              type="submit"
              :disabled="isLoading || !inputText.trim()"
              class="cursor-pointer h-10 w-10 flex items-center justify-center rounded-xl bg-amber-400 dark:bg-amber-500 text-stone-900 hover:bg-amber-500 dark:hover:bg-amber-400 disabled:opacity-40 transition-colors"
            >
              <Icon icon="mdi:send" class="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </transition>

    <!-- FAB -->
    <button
      type="button"
      @click="isOpen = !isOpen"
      :class="[
        'group flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full shadow-lg border-2 transition-all duration-300',
        isOpen
          ? 'bg-stone-700 dark:bg-stone-800 border-stone-500 text-white'
          : 'bg-gradient-to-r from-stone-200 to-amber-200 dark:from-stone-700 dark:to-amber-800 border-amber-400/60 dark:border-amber-500/50 text-stone-800 dark:text-amber-100 hover:shadow-xl hover:scale-105',
      ]"
      :aria-label="t('chat.openChat')"
    >
      <div
        class="w-8 h-8 rounded-full bg-stone-500 dark:bg-stone-600 flex items-center justify-center ring-2 ring-amber-400/40"
      >
        <Icon icon="mdi:ghost" class="w-5 h-5 text-white" />
      </div>
      <span v-if="!isOpen" class="text-sm font-semibold pr-1">{{ t("common.ghost") }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";
import { useChat } from "@/composables/useChat";

const { t, locale } = useI18n();
const { messages, isLoading, sendMessage, clearChat, setWelcomeMessage } = useChat();

watch(locale, () => {
  if (messages.value.length === 1 && messages.value[0]?.id === "welcome") {
    setWelcomeMessage(t("chat.welcome"));
  }
});

const isOpen = ref(false);
const inputText = ref("");
const messagesRef = ref<HTMLElement | null>(null);

const scrollToBottom = async (): Promise<void> => {
  await nextTick();
  if (messagesRef.value) {
    messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
  }
};

const handleSubmit = async (): Promise<void> => {
  const text = inputText.value;
  inputText.value = "";
  await sendMessage(text);
  await scrollToBottom();
};

watch(messages, scrollToBottom, { deep: true });
watch(isLoading, scrollToBottom);
</script>

<style scoped>
.chat-panel-enter-active,
.chat-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.chat-panel-enter-from,
.chat-panel-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}
</style>
